<template>
  <section class="carousel-section" id="home" ref="carouselSection">
    <div
        class="carousel-container"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
    >
      <div
          v-for="(slide, index) in slides"
          :key="index"
          class="carousel-slide"
          :class="{
          active: currentSlide === index,
          prev: (currentSlide - 1 + slides.length) % slides.length === index,
          next: (currentSlide + 1) % slides.length === index
        }"
          :style="{ backgroundImage: `url(${getImageUrl(slide.image)})` }"
          :aria-hidden="currentSlide !== index"
          :aria-label="`Slide ${index + 1}`"
          role="group"
      >
        <div class="image-overlay"></div>
      </div>

      <button
          class="carousel-arrow arrow-left"
          @click="prevSlide"
          aria-label="Previous slide"
      >
        <svg class="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button
          class="carousel-arrow arrow-right"
          @click="nextSlide"
          aria-label="Next slide"
      >
        <svg class="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="carousel-dots">
        <button
            v-for="(slide, index) in slides"
            :key="index"
            class="carousel-dot"
            :class="{ active: currentSlide === index }"
            @click="goToSlide(index)"
            :aria-label="`Go to slide ${index + 1}`"
            :aria-current="currentSlide === index"
        >
          <span class="dot-progress" v-if="currentSlide === index"></span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'Carousel',
  data() {
    return {
      currentSlide: 0,
      slideInterval: null,
      touchStartX: 0,
      touchEndX: 0,
      isAnimating: false,
      slides: [
        { image: 'carousel1.jpg' },
        { image: 'carousel2.jpg' },
        { image: 'carousel3.jpg' }
        ]
    };
  },
  mounted() {
    this.startCarousel();
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    this.pauseCarousel();
    window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    getImageUrl(imageName) {
      return require(`@/assets/carousel/${imageName}`);
    },
    startCarousel() {
      this.pauseCarousel();
      this.slideInterval = setInterval(() => {
        if (!this.isAnimating) {
          this.nextSlide();
        }
      }, 4000);
    },
    nextSlide() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      this.restartCarousel();
      setTimeout(() => {
        this.isAnimating = false;
      }, 800);
    },
    prevSlide() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.restartCarousel();
      setTimeout(() => {
        this.isAnimating = false;
      }, 800);
    },
    goToSlide(index) {
      if (this.isAnimating || index === this.currentSlide) return;
      this.isAnimating = true;
      this.currentSlide = index;
      this.restartCarousel();
      setTimeout(() => {
        this.isAnimating = false;
      }, 800);
    },
    pauseCarousel() {
      if (this.slideInterval) {
        clearInterval(this.slideInterval);
        this.slideInterval = null;
      }
    },
    resumeCarousel() {
    },
    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    },
    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    },
    handleSwipe() {
      if (this.isAnimating) return;

      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
    },
    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (event.key === 'ArrowRight') {
        this.nextSlide();
      } else if (event.key >= '1' && event.key <= '4') {
        const index = parseInt(event.key) - 1;
        if (index < this.slides.length) {
          this.goToSlide(index);
        }
      }
    },
    restartCarousel() {
      this.pauseCarousel();
      this.slideInterval = setInterval(() => {
        if (!this.isAnimating) {
          this.nextSlide();
        }
      }, 4000);
    }
  }
};
</script>

<style scoped>
.carousel-section {
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background: #fafafa;
}

.carousel-container {
  width: 100%;
  height: 100%;
  position: relative;
  cursor: pointer;
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  transition: opacity 0.8s ease, transform 0.8s ease;
  opacity: 0;
  transform: scale(1.05);
  z-index: 1;
  border-radius: 0;
}

.carousel-slide.active {
  opacity: 1;
  transform: scale(1);
  z-index: 3;
}

.carousel-slide.prev {
  opacity: 0;
  transform: translateX(-30%) scale(0.95);
  z-index: 2;
}

.carousel-slide.next {
  opacity: 0;
  transform: translateX(30%) scale(0.95);
  z-index: 2;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
      135deg,
      rgba(154, 27, 30, 0.1) 0%,
      rgba(178, 34, 34, 0.15) 50%,
      rgba(154, 27, 30, 0.05) 100%
  );
  pointer-events: none;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(154, 27, 30, 0.9);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  border: 2px solid rgba(255, 255, 255, 0.3);
  outline: none;
  box-shadow: 0 4px 20px rgba(154, 27, 30, 0.4);
}

.carousel-arrow:hover {
  background: rgba(178, 34, 34, 0.95);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-50%) scale(1.15);
  box-shadow: 0 6px 25px rgba(178, 34, 34, 0.6);
}

.carousel-arrow:active {
  transform: translateY(-50%) scale(0.95);
}

.arrow-icon {
  width: 28px;
  height: 28px;
  stroke: white;
  stroke-width: 3;
  transition: transform 0.3s ease;
}

.carousel-arrow:hover .arrow-icon {
  transform: scale(1.1);
}

.arrow-left {
  left: 40px;
}

.arrow-right {
  right: 40px;
}

.carousel-dots {
  position: absolute;
  bottom: 50px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 20px;
  z-index: 10;
}

.carousel-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  outline: none;
  position: relative;
  padding: 0;
  overflow: hidden;
}

.carousel-dot:hover {
  background-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.carousel-dot.active {
  background-color: transparent;
  border-color: #9A1B1E;
  transform: scale(1.2);
}

.dot-progress {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #9A1B1E 0%, #B22222 100%);
  border-radius: 50%;
  animation: progressAnimation 4s linear infinite;
}

@keyframes progressAnimation {
  0% {
    clip-path: circle(0% at 50% 50%);
  }
  100% {
    clip-path: circle(100% at 50% 50%);
  }
}

.carousel-dot.active::after {
  content: '';
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border-radius: 50%;
  border: 2px solid rgba(154, 27, 30, 0.4);
  animation: pulseHalo 2s ease-in-out infinite;
}

@keyframes pulseHalo {
  0%, 100% {
    transform: scale(1);
    opacity: 0.4;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.2;
  }
}

@media (max-width: 1024px) {
  .carousel-section {
    height: 90vh;
  }
}

@media (max-width: 768px) {
  .carousel-section {
    height: 80vh;
  }

  .carousel-arrow {
    width: 50px;
    height: 50px;
  }

  .arrow-icon {
    width: 24px;
    height: 24px;
  }

  .arrow-left {
    left: 20px;
  }

  .arrow-right {
    right: 20px;
  }

  .carousel-dots {
    bottom: 35px;
    gap: 16px;
  }

  .carousel-dot {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 576px) {
  .carousel-section {
    height: 70vh;
  }

  .carousel-arrow {
    width: 44px;
    height: 44px;
  }

  .arrow-icon {
    width: 20px;
    height: 20px;
  }

  .arrow-left {
    left: 15px;
  }

  .arrow-right {
    right: 15px;
  }

  .carousel-dots {
    bottom: 30px;
    gap: 14px;
  }

  .carousel-dot {
    width: 12px;
    height: 12px;
  }
}

@media (min-width: 1920px) {
  .carousel-slide {
    background-size: contain;
    background-color: #fafafa;
  }
}

.carousel-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg,
  transparent,
  #9A1B1E 30%,
  #B22222 70%,
  transparent);
  z-index: 5;
  opacity: 0.7;
}

.carousel-container::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg,
  transparent,
  #B22222 30%,
  #9A1B1E 70%,
  transparent);
  z-index: 5;
  opacity: 0.7;
}

@media (prefers-reduced-motion: reduce) {
  .carousel-slide,
  .carousel-arrow,
  .carousel-dot,
  .dot-progress {
    transition: none;
    animation: none;
  }

  .carousel-dot.active::after {
    animation: none;
  }
}
</style>